import React from "react";
import { ActionIcon, createStyles, Divider } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { Button, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import { Link, useOutletContext } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { formatErrorDetail } from "../../../utils/query.util";
import { Body } from "../../../components/Typography";
import { DetailField } from "./order-details/DetailField";
import { PaymentCard } from "./order-details/PaymentCard";
import { RefundCard } from "./order-details/RefundCard";
import {
  flexColumn,
  formatOrderDate,
  formatUosDateTime,
  linkStyle,
  OrderViewOutletContext,
} from "./order-details/shared";
import { SkeletonField } from "../../../components/skeleton/SkeletonField";
import { SkeletonCard } from "../../../components/skeleton/SkeletonCard";

const useStyles = createStyles(() => ({
  container: {
    ...flexColumn,
    gap: tokens.spacing[6],
    padding: tokens.spacing[4],
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: tokens.spacing[6],
    paddingLeft: tokens.spacing[4],
    paddingRight: tokens.spacing[4],
  },
  cardsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: tokens.spacing[6],
  },
  link: linkStyle,
  reprintRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
  },
  errorContainer: {
    ...flexColumn,
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing[10],
    gap: tokens.spacing[4],
  },
  subtleText: {
    color: tokens.color.semantic.text.subtle,
  },
}));

const OrderDetailsSkeleton: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <SkeletonField />
      </div>
      <div className={classes.row}>
        <SkeletonField />
        <SkeletonField />
      </div>
      <Divider />
      <div className={classes.cardsRow}>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );
};

const ReprintOrderType: React.FC<{ customerPublicId: string; customerOrderId: string; parentOrderId: string }> = ({
  customerPublicId,
  customerOrderId,
  parentOrderId,
}) => {
  const { classes } = useStyles();
  const clipboard = useClipboard({ timeout: 1500 });

  return (
    <div className={classes.reprintRow}>
      <Body>
        Reprint of{" "}
        <Link
          to={getRoutePath("order:details", {
            customerPublicId,
            customerOrderId,
          })}
          className={classes.link}
        >
          Order# {parentOrderId}
        </Link>
      </Body>
      <ActionIcon size="xs" variant="transparent" onClick={() => clipboard.copy(parentOrderId)}>
        <IconV3 icon="content_copy" size={16} />
      </ActionIcon>
    </div>
  );
};

export const OrderDetailsTab: React.FC = () => {
  const { customerPublicId, customerOrderId } = useOutletContext<OrderViewOutletContext>();
  const user = useAuthUser();
  const { classes } = useStyles();

  const { data, isLoading, error, refetch } = GraphQL.useOrderDetailsQuery({
    orderIdentifier: {
      customerPublicId,
      customerOrderId,
    },
    isSandbox: user.isSandbox,
  });

  const order = data?.orderByIdentifier;

  if (isLoading) {
    return <OrderDetailsSkeleton />;
  }

  if (error || !order) {
    const errorDetail = formatErrorDetail(error);
    return (
      <div className={classes.errorContainer}>
        <Body>Failed to load order details.</Body>
        {errorDetail && (
          <Body size="sm" className={classes.subtleText}>
            {errorDetail}
          </Body>
        )}
        <Button variant="outlined" intent="neutral" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="order-details-loaded" className={classes.container}>
      <div className={classes.row}>
        <DetailField label="Order Type">
          {order.reprintParentCustomerOrderId ? (
            <ReprintOrderType
              customerPublicId={customerPublicId}
              customerOrderId={customerOrderId}
              parentOrderId={order.reprintParentCustomerOrderId}
            />
          ) : (
            "Original"
          )}
        </DetailField>
        <DetailField label="Last Status Message" testId="order-details-last-status-message">
          {order.lastStatusMessage?.trim() || "—"}
        </DetailField>
      </div>

      <div className={classes.row}>
        <DetailField label="Order Date">{order.createdAt ? formatOrderDate(order.createdAt) : "—"}</DetailField>
        {user.is.SUPER_ADMIN && (
          <DetailField label="Organization">
            <Link to={getRoutePath("customer", { publicId: customerPublicId })} className={classes.link}>
              {order.customer?.organizationName ?? customerPublicId}
            </Link>
          </DetailField>
        )}
      </div>

      {user.is.SUPER_ADMIN && order.submittedAt && (
        <div className={classes.row}>
          <DetailField label="Submitted to UOS" testId="order-details-uos-submitted">
            {formatUosDateTime(order.submittedAt)}
          </DetailField>
        </div>
      )}

      <Divider />

      <div className={classes.cardsRow}>
        <PaymentCard order={order} isSandbox={user.isSandbox} />
        {!user.isSandbox && <RefundCard order={order} />}
      </div>
    </div>
  );
};
